export type ArticleDefinition = {
  slug: string;
  path: `/article/${string}`;
  title: string;
  description: string;
  summary: string;
  structuredDescription?: string;
  section: string;
  tags: readonly string[];
  /** The person who wrote it, by name; their photo is looked up in `authorPhotos`. */
  author: string;
  publishedTime: string;
  modifiedTime: string;
  contentModule: `../articles/${string}ArticleContent.tsx`;
};

/**
 * Post-nominal letters printed after an author's name in the byline. Kept out of `author`, which
 * stays the plain name: it keys `authorPhotos` and is the schema.org `name`, where a search engine
 * expects the name alone (the suffix goes to `honorificSuffix` instead).
 */
export const authorHonorificSuffixes: Readonly<Record<string, string>> = {
  "Daniel Cervenkov": "Ph.D.",
};

export const BLOG_PATH = "/blog";
export const BLOG_DESCRIPTION =
  "Insights, guides and updates on clipping, creator distribution and campaign measurement.";

/**
 * The single registration point for published articles. Add real metadata and the body module
 * path here; the homepage, listing, article routes, metadata, prerender and sitemap follow.
 * Dates describe the writing: preserve publishedTime and update modifiedTime only for copy changes.
 * ArticlePage resolves contentModule through a lazy glob. Keeping this file free of body imports
 * also lets the sitemap/prerender tooling consume it without loading React, figures or JSX.
 */
export const articleRegistry: readonly ArticleDefinition[] = [
  {
    slug: "future-of-clipping",
    path: "/article/future-of-clipping",
    title: "Clipping Marketing Is at a Crossroads - Here's What Decides Its Future",
    description:
      "Clipping is one of the most efficient ways to reach a high-intent audience, but fake views threaten it. Why verification decides whether clipping matures into a performance channel.",
    summary:
      "Clipping is becoming a real line item in marketing budgets. Whether it matures or fades depends on what happens to fake views.",
    structuredDescription:
      "What clipping marketing is, why it is growing, and the two futures it faces: a maturing performance channel with verified views, or a trust recession driven by botted views that make up 5-40% of a typical campaign.",
    section: "Industry",
    tags: [
      "Clipping campaigns",
      "Clipping marketing",
      "Short-form video advertising",
      "View botting",
      "Ad fraud detection",
    ],
    author: "Matthew Hrabkovsky",
    publishedTime: "2026-10-22",
    modifiedTime: "2026-10-22",
    contentModule: "../articles/FutureOfClippingArticleContent.tsx",
  },
  {
    slug: "brand-safety",
    path: "/article/brand-safety",
    title: "How We Keep Brands Safe in Clipping",
    description:
      "How Evangelist checks every clip for brand safety, real use of campaign material, and brief compliance, with evidence-backed decisions and blinded human review.",
    summary:
      "Three checks on every clip: safe and suitable content, real use of campaign material, and a finished edit that follows the brief.",
    structuredDescription:
      "How Evangelist evaluates creator clips against advertiser requirements: IAB-aligned brand-safety scoring across frames, speech, on-screen text and metadata; visual-embedding and audio-fingerprint matching against approved assets; brief-compliance checks; and commercially blinded human review with appeals.",
    section: "Brand safety",
    tags: [
      "Brand safety",
      "Brand suitability",
      "Clipping campaigns",
      "Short-form video advertising",
      "Content moderation",
    ],
    author: "Daniel Cervenkov",
    publishedTime: "2026-10-11",
    modifiedTime: "2026-10-11",
    contentModule: "../articles/BrandSafetyArticleContent.tsx",
  },
  {
    slug: "antibotting",
    path: "/article/antibotting",
    title: "How We Keep Botting Out of Clipping",
    description:
      "How Evangelist detects fake engagement and verifies creator analytics: full-lifetime telemetry, an ensemble detection model, and blind human review.",
    summary: "Two lines of defense against fake engagement: full-lifetime detection and verified creator analytics.",
    structuredDescription:
      "How Evangelist detects fake engagement and verifies reported creator analytics: full-lifetime telemetry, an ensemble detection model, blind human review, and zero-knowledge cryptographic verification.",
    section: "Fraud prevention",
    tags: [
      "Ad fraud detection",
      "View botting",
      "Clipping campaigns",
      "Short-form video advertising",
      "zkTLS verification",
    ],
    author: "Daniel Cervenkov",
    publishedTime: "2026-08-23",
    modifiedTime: "2026-08-23",
    contentModule: "../articles/AntibottingArticleContent.tsx",
  },
];
